/**
 * Fixes SVG local-reference rendering for inline logo SVGs that rely on
 * `<mask>` / `<linearGradient>` / `<clipPath>` referenced via `url(#id)`.
 *
 * Two problems are addressed:
 *
 * 1. Duplicate element IDs — the same logo can render more than once on a
 *    page (e.g. header + footer), and both copies ship identical hard-coded
 *    IDs. `url(#id)` then resolves to the first match in the document, so one
 *    of the copies paints against the wrong mask/gradient and renders blank.
 *
 * 2. The `<base href>` tag in `index.html` breaks `url(#id)` fragment
 *    references under history-mode routing on some browsers: the fragment is
 *    resolved against the base URI instead of the current document, so the
 *    referenced def is never found and the shape renders unmasked/blank.
 *
 * On mount every ID inside the component's root `<svg>` is rewritten to a
 * value unique to this instance, and every `url(#id)` reference is pointed at
 * the current document URL so the `<base>` tag cannot misresolve it. The
 * references are refreshed on route change because the header/footer logos
 * persist across navigation.
 *
 * The component must have an `<svg>` root element.
 */

// Presentation attributes that can carry a `url(#id)` functional reference.
const REF_ATTRS = [
  'mask',
  'clip-path',
  'fill',
  'stroke',
  'filter',
  'marker-start',
  'marker-mid',
  'marker-end'
]

// Matches `url(#fragment)` (optionally quoted / already prefixed / already
// suffixed by a previous run) and captures the base fragment name.
const URL_REF_RE = /url\(\s*["']?[^#)"']*#([^)"'\s]+?)(?:__svgref\d+)?["']?\s*\)/g

export default {
  mounted () {
    this.$_svgRefUniquify()
    this.$_svgRefRefresh()
  },
  watch: {
    $route () {
      this.$_svgRefRefresh()
    }
  },
  methods: {
    $_svgRefSuffix () {
      return `__svgref${this._uid}`
    },
    // Rename every id in the root svg to a per-instance unique value. Runs
    // once; re-running is a no-op because ids already carry the suffix.
    $_svgRefUniquify () {
      const root = this.$el
      if (!root || typeof root.querySelectorAll !== 'function') return
      const suffix = this.$_svgRefSuffix()
      const idEls = root.querySelectorAll('[id]')
      for (let i = 0; i < idEls.length; i++) {
        const el = idEls[i]
        const id = el.getAttribute('id')
        if (!id || id.indexOf(suffix) !== -1) continue
        el.setAttribute('id', id + suffix)
      }
    },
    // Point every url(#id) reference at the current document + the unique id,
    // so it survives the <base> tag and duplicate ids. Idempotent across runs.
    $_svgRefRefresh () {
      const root = this.$el
      if (!root || typeof root.querySelectorAll !== 'function') return
      if (typeof window === 'undefined') return
      const suffix = this.$_svgRefSuffix()
      const base = window.location.pathname + window.location.search
      const remap = (value) => {
        if (!value || value.indexOf('url(') === -1) return value
        return value.replace(URL_REF_RE, (match, fragment) => {
          return 'url(' + base + '#' + fragment + suffix + ')'
        })
      }
      const nodes = [root]
      const descendants = root.querySelectorAll('*')
      for (let i = 0; i < descendants.length; i++) nodes.push(descendants[i])
      for (let n = 0; n < nodes.length; n++) {
        const el = nodes[n]
        if (!el.getAttribute) continue
        for (let a = 0; a < REF_ATTRS.length; a++) {
          const attr = REF_ATTRS[a]
          if (el.hasAttribute(attr)) {
            const current = el.getAttribute(attr)
            const next = remap(current)
            if (next !== current) el.setAttribute(attr, next)
          }
        }
        if (el.hasAttribute('style')) {
          const current = el.getAttribute('style')
          const next = remap(current)
          if (next !== current) el.setAttribute('style', next)
        }
      }
    }
  }
}
